import { motion } from 'motion/react';
import { ArrowRight, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { CASE_STUDIES } from '../shared/constants';

interface CaseStudyCardProps {
  id?: string;
  title: string;
  industry: string;
  challenge: string;
  solution: string;
  results: string[];
  image: string;
  metrics: { value: string; label: string }[];
  delay?: number;
}

function CaseStudyCard({ id, title, industry, challenge, solution, results, image, metrics, delay = 0 }: CaseStudyCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true }}
      whileHover={{ y: -10 }}
      className="h-full"
    >
      <Link to={id ? `/case-studies/${id}` : '#'} className="block h-full">
        <Card className="glass h-full hover:border-primary/50 transition-all duration-500 overflow-hidden cursor-pointer group">
          <div className="relative">
            <ImageWithFallback
              src={image}
              alt={title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute top-4 left-4 bg-primary/90 text-black px-3 py-1 rounded-full text-sm font-semibold">
              {industry}
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-xl font-bold mb-4 group-hover:text-primary transition-colors duration-300">{title}</h3>

            <div className="space-y-4 mb-6">
              <div>
                <h4 className="font-semibold text-destructive mb-2">Challenge</h4>
                <p className="text-sm text-muted-foreground line-clamp-2">{challenge}</p>
              </div>

              <div>
                <h4 className="font-semibold text-primary mb-2">Solution</h4>
                <p className="text-sm text-muted-foreground line-clamp-2">{solution}</p>
              </div>

              <div>
                <h4 className="font-semibold text-accent mb-2">Results</h4>
                <ul className="space-y-1">
                  {results.slice(0, 2).map((result, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 mr-2 flex-shrink-0" />
                      {result}
                    </li>
                  ))}
                  {results.length > 2 && (
                    <li className="text-sm text-muted-foreground italic">
                      +{results.length - 2} more results...
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {metrics.map((metric, index) => (
                <div key={index} className="text-center glass-strong p-3 rounded-lg">
                  <div className="font-bold text-primary">{metric.value}</div>
                  <div className="text-xs text-muted-foreground">{metric.label}</div>
                </div>
              ))}
            </div>

            <Button variant="outline" className="w-full rounded-full border-primary/50 hover:border-primary hover:bg-primary/10 group-hover:neon-glow transition-all duration-300">
              View Full Study
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

export function CaseStudiesPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-15 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div className="absolute inset-0 pattern-background" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Award className="w-12 h-12 text-primary mr-4" />
              <span className="text-primary font-semibold">Success Stories</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 neon-text">
              Real Results, Real Impact
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground text-center mb-8">
              Discover how leading organizations have transformed their operations
              and achieved measurable results with our AI solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Featured Case Studies</h2>
            <p className="text-xl text-muted-foreground max-w-3xl text-center mx-auto">
              Explore detailed success stories across industries and discover
              the transformative power of AI-driven solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {CASE_STUDIES.map((study, index) => (
              <CaseStudyCard
                key={study.title}
                {...study}
                delay={index * 0.2}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Proven Track Record</h2>
            <p className="text-xl text-muted-foreground max-w-3xl text-center mx-auto">
              Our AI solutions consistently deliver exceptional results across all implementations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { value: "500+", label: "Successful Deployments" },
              { value: "99.7%", label: "Average Model Accuracy" },
              { value: "$50M+", label: "Client Cost Savings" },
              { value: "95%", label: "Client Satisfaction Rate" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2 neon-text">{stat.value}</div>
                <p className="text-muted-foreground text-center">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-15 bg-gradient-to-r from-primary/10 to-secondary/10 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to Write Your Success Story?</h2>
            <p className="text-xl text-muted-foreground mb-8 text-center max-w-3xl mx-auto">
              Join the ranks of industry leaders who have transformed their operations with our AI solutions
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r bg-primary text-primary-foreground hover:neon-glow px-8 py-4 rounded-xl font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            {/* <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:neon-glow px-8 py-4 text-lg">
              Start Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button> */}
          </motion.div>
        </div>
      </section>
    </div>
  );
}